import { useRef, useState } from "react";
import Nav from "../components/Nav";
import Footer from "../components/Footer";
import Reveal from "../components/Reveal";
import Modal from "../components/Modal";
import PdfPageCanvas from "../components/PdfPageCanvas";
import CredlyBadge from "../components/CredlyBadge";
import certificates from "../data/certificates";

const CERT_BASE = `${process.env.PUBLIC_URL}/certificates/`;
const THUMB_BASE = `${CERT_BASE}thumbnails/`;
const badgedCertificates = certificates.filter((c) => c.credlyId);
const PAGE_SIZE = 12;

// PDFs ship with a pre-rendered PNG of their first page (see
// scripts/generate-certificate-thumbnails.js) so the gallery never has to
// render a PDF client-side. If a PDF hasn't been thumbnailed yet, this falls
// back to a live PdfPageCanvas render so a freshly dropped file still works.
function CertImage({ cert, pdfWidth, className }) {
  const [thumbFailed, setThumbFailed] = useState(false);

  if (cert.type === "pdf" && cert.thumbnail && !thumbFailed) {
    return (
      <img
        src={THUMB_BASE + cert.thumbnail}
        alt={cert.title}
        decoding="async"
        className={className}
        onError={() => setThumbFailed(true)}
      />
    );
  }
  if (cert.type === "pdf") {
    return <PdfPageCanvas url={CERT_BASE + cert.file} pageNumber={1} width={pdfWidth} />;
  }
  return <img src={CERT_BASE + cert.file} alt={cert.title} decoding="async" className={className} />;
}

export default function Certificates() {
  const [active, setActive] = useState(null);
  const [page, setPage] = useState(1);
  const galleryRef = useRef(null);

  const totalPages = Math.max(1, Math.ceil(certificates.length / PAGE_SIZE));
  const pageCertificates = certificates.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const goToPage = (p) => {
    setPage(p);
    galleryRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <>
      <Nav />

      <div className="mx-auto max-w-content px-6 pb-14 pt-32 md:px-12 md:pb-20 md:pt-40">
        <p className="eyebrow">Credentials</p>
        <h1 className="mt-3 font-serif text-4xl font-extrabold leading-tight text-heading md:text-5xl">
          Certificates &amp; Qualifications
        </h1>
        <p className="mt-5 max-w-[52ch] text-[0.95rem] leading-relaxed text-ink-soft">
          Certifications completed across occupational health &amp; safety, agile project management, and
          web development. Select any item for a full-size view.
        </p>
      </div>

      {badgedCertificates.length > 0 && (
        <section className="border-y border-line bg-surface-alt">
          <div className="mx-auto max-w-content px-6 py-14 md:px-12">
            <p className="eyebrow">Verified Digital Badges</p>
            <h2 className="mt-2 font-serif text-2xl text-heading">Confirm authenticity on Credly</h2>
            <p className="mt-3 max-w-[62ch] text-[0.9rem] leading-relaxed text-ink-soft">
              These credentials are digitally verified, the badge below is live and interactive; select it
              or the verify link to confirm the issuer, date, and recipient directly on Credly.
            </p>

            <div className="mt-8 grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
              {badgedCertificates.map((cert) => (
                <Reveal
                  key={cert.id}
                  className="flex flex-col items-center gap-4 border border-line-strong bg-surface p-6 text-center"
                >
                  <CredlyBadge badgeId={cert.credlyId} />
                  <div>
                    <p className="font-serif text-base text-heading">{cert.title}</p>
                    <p className="mt-1 text-[0.78rem] text-ink-faint">{cert.issuer}</p>
                  </div>
                  {cert.verifyUrl && (
                    <a
                      href={cert.verifyUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="text-[0.75rem] uppercase tracking-widest2 text-link hover:text-heading"
                    >
                      Verify on Credly &rarr;
                    </a>
                  )}
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      <div ref={galleryRef} className="mx-auto max-w-content px-6 py-14 md:px-12">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="eyebrow">All Certificates</p>
            <h2 className="mt-2 font-serif text-2xl text-heading">Full Gallery</h2>
          </div>
          <p className="text-[0.75rem] uppercase tracking-widest2 text-ink-faint">
            {certificates.length} certificates &middot; page {page} of {totalPages}
          </p>
        </div>
        <div className="mt-8 grid gap-px border border-line bg-line sm:grid-cols-2 lg:grid-cols-3">
          {pageCertificates.map((cert, i) => (
            <Reveal
              as="button"
              key={cert.id}
              delay={i * 60}
              onClick={() => setActive(cert)}
              className="relative flex flex-col bg-surface text-left transition-colors hover:bg-surface-alt"
            >
              {cert.verifyUrl && (
                <span className="absolute right-3 top-3 z-10 bg-forest-900 px-2 py-1 text-[0.75rem] uppercase tracking-widest2 text-white">
                  Verified
                </span>
              )}
              <div className="flex aspect-[4/3] items-center justify-center overflow-hidden bg-surface-alt p-4 [&_canvas]:max-h-full [&_canvas]:w-auto">
                <CertImage cert={cert} pdfWidth={260} className="max-h-full max-w-full object-contain" />
              </div>
              <div className="border-t border-line px-6 py-5">
                <p className="font-serif text-base text-heading">{cert.title}</p>
                <p className="mt-1 text-[0.78rem] text-ink-faint">{cert.issuer}</p>
              </div>
            </Reveal>
          ))}
        </div>

        {totalPages > 1 && (
          <div className="mt-8 flex items-center justify-between gap-4">
            <button
              onClick={() => goToPage(Math.max(1, page - 1))}
              disabled={page <= 1}
              className="border border-line-strong px-5 py-2.5 text-[0.75rem] uppercase tracking-widest2 text-ink-soft transition-colors hover:border-forest-900 hover:text-heading disabled:opacity-30"
            >
              &larr; Prev
            </button>
            <div className="flex gap-2">
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                <button
                  key={p}
                  onClick={() => goToPage(p)}
                  aria-current={p === page ? "page" : undefined}
                  className={`h-9 w-9 text-[0.8rem] transition-colors ${
                    p === page
                      ? "bg-forest-900 text-white"
                      : "border border-line-strong text-ink-soft hover:border-forest-900 hover:text-heading"
                  }`}
                >
                  {p}
                </button>
              ))}
            </div>
            <button
              onClick={() => goToPage(Math.min(totalPages, page + 1))}
              disabled={page >= totalPages}
              className="border border-line-strong px-5 py-2.5 text-[0.75rem] uppercase tracking-widest2 text-ink-soft transition-colors hover:border-forest-900 hover:text-heading disabled:opacity-30"
            >
              Next &rarr;
            </button>
          </div>
        )}
      </div>

      <Modal
        open={!!active}
        onClose={() => setActive(null)}
        title={active?.title || ""}
        footer={active && (
          <>
            {active.verifyUrl && (
              <a
                href={active.verifyUrl}
                target="_blank"
                rel="noreferrer"
                className="border border-line-strong px-6 py-2.5 text-[0.75rem] uppercase tracking-widest2 text-ink-soft transition-colors hover:border-forest-900 hover:text-heading"
              >
                Verify on Credly &rarr;
              </a>
            )}
            <a
              href={CERT_BASE + active.file}
              target="_blank"
              rel="noreferrer"
              className="border border-forest-900 bg-forest-900 px-6 py-2.5 text-[0.75rem] uppercase tracking-widest2 text-white transition-colors hover:bg-forest-700"
            >
              Open Original &rarr;
            </a>
          </>
        )}
      >
        {active && (
          <CertImage cert={active} pdfWidth={760} className="max-h-[76vh] w-auto max-w-full object-contain" />
        )}
      </Modal>

      <Footer />
    </>
  );
}
